import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { ASSET_CONDITIONS } from "../../../constants/inventory";
import { IoArrowBack } from "react-icons/io5";

const AssignAsset = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();

  const [item, setItem] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [project, setProject] = useState("");
  const [department, setDepartment] = useState("");
  const [assignedDate, setAssignedDate] = useState("");
  const [expectedReturnDate, setExpectedReturnDate] = useState("");
  const [condition, setCondition] = useState("new");
  const [notes, setNotes] = useState("");

  const [inventoryItems, setInventoryItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async () => {
    try {
      const [itemsRes, staffRes, projectsRes, deptsRes] = await Promise.all([
        fetch("/api/getAllInventoryItems?limit=200", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch("/api/getallStaffInfo", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch("/api/getAllProjects?limit=100", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch("/api/getAllDepartments", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const itemsData = await itemsRes.json();
      const staffData = await staffRes.json();
      const projectsData = await projectsRes.json();
      const deptsData = await deptsRes.json();

      if (itemsRes.ok) {
        const serializedItems = (itemsData.items || []).filter(
          (i) => i.isSerialized || i.trackSerialNumbers
        );
        setInventoryItems(serializedItems.length > 0 ? serializedItems : itemsData.items || []);
      }
      if (staffRes.ok) setEmployees(staffData.staffInfo || staffData || []);
      if (projectsRes.ok) setProjects(projectsData.projects || []);
      if (deptsRes.ok) setDepartments(deptsData.departments || deptsData || []);
    } catch (err) {
      setErrorMessage("Failed to load form data");
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const validateForm = () => {
    if (!item) return "Please select an item";
    if (!assignedTo) return "Please select an employee";
    if (!assignedDate) return "Please select an assigned date";
    if (!condition) return "Please select a condition";
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setErrorMessage(validationError);
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const response = await fetch("/api/assignAsset", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          item,
          serialNumber: serialNumber || undefined,
          assignedTo,
          project: project || undefined,
          department: department || undefined,
          assignedDate,
          expectedReturnDate: expectedReturnDate || undefined,
          condition,
          notes: notes || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setErrorMessage(data.error || "Failed to assign asset");
        setTimeout(() => setErrorMessage(""), 3000);
        return;
      }

      setSuccessMessage("Asset assigned successfully");
      setTimeout(() => {
        navigate("/Assets");
      }, 1500);
    } catch (err) {
      setErrorMessage("Failed to assign asset");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate("/Assets")}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <IoArrowBack className="text-xl text-primary" />
        </button>
        <h2 className="text-xl font-semibold text-gray-800">Assign Asset</h2>
      </div>

      {/* Messages */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {successMessage}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Item */}
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Item <span className="text-red-500">*</span>
            </label>
            <select
              value={item}
              onChange={(e) => setItem(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Item</option>
              {inventoryItems.map((inv) => (
                <option key={inv._id} value={inv._id}>
                  {inv.sku} - {inv.name}
                </option>
              ))}
            </select>
          </div>

          {/* Serial Number */}
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Serial Number
            </label>
            <input
              type="text"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              placeholder="Enter serial number"
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Assigned To */}
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Assigned To <span className="text-red-500">*</span>
            </label>
            <select
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Employee</option>
              {employees.map((emp) => (
                <option key={emp._id} value={emp._id}>
                  {emp.employeeId ? `${emp.employeeId} - ` : ""}
                  {emp.name || `${emp.firstName || ""} ${emp.lastName || ""}`.trim()}
                </option>
              ))}
            </select>
          </div>

          {/* Project */}
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Project
            </label>
            <select
              value={project}
              onChange={(e) => setProject(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Project (Optional)</option>
              {projects.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.projectCode} - {p.name}
                </option>
              ))}
            </select>
          </div>

          {/* Department */}
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Department
            </label>
            <select
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Department (Optional)</option>
              {departments.map((d) => (
                <option key={d._id} value={d._id}>
                  {d.deptName}
                </option>
              ))}
            </select>
          </div>

          {/* Condition */}
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Condition <span className="text-red-500">*</span>
            </label>
            <select
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {ASSET_CONDITIONS.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Assigned Date */}
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Assigned Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={assignedDate}
              onChange={(e) => setAssignedDate(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Expected Return Date */}
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Expected Return Date
            </label>
            <input
              type="date"
              value={expectedReturnDate}
              onChange={(e) => setExpectedReturnDate(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Notes */}
        <div className="mb-6">
          <label className="block font-medium mb-2 text-blue-500">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Additional notes about this assignment"
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate("/Assets")}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "Assigning..." : "Assign Asset"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignAsset;
